import React, { useEffect, useState } from 'react';
import { isMobile, isMobileOnly } from 'react-device-detect';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { get, isEmpty } from 'lodash-es';
import styled, { css } from 'styled-components';
import queryString from 'query-string';
import {
  CenterBox,
  Body2,
  DoconInput,
  FlexBox,
  InputPrefix,
  PrimaryButton,
  LinkButton,
  message,
} from '@docon/docon-ui-library';
import { forgotPassword, login, Status, updateAuthState } from 'redux/reducer/auth';
import Image from 'shared/Image';
import { DoconLogo } from 'shared/icons';
import { eventService, storageService } from 'services';
import { getNextPathname, getPageName } from 'utils';
import { colors } from 'utilities/colorConfig';
import cleverTapAnl from 'utilities/cleverTapAnalytics';

const Section = styled(CenterBox)`
  place-content: center;

  &:first-child {
    background-color: ${colors.primaryBlue};
  }
`;

const Form = styled(FlexBox)`
  position: relative;
  padding: 30px 50px;
  flex-direction: column;
  gap: 25px;
  width: 400px;
  border-radius: 6px;
  background-color: ${colors.white};
  box-shadow: 0 0 4px 0 rgb(0 0 0 / 10%);

  ${InputPrefix} {
    color: ${colors.subHeading};
    width: 50px;
  }
`;

const ErrorText = styled(Body2)`
  position: absolute;
  left: 0;
  bottom: 10px;
  width: 100%;
  padding-top: 8px;
  font-weight: 500;
  white-space: pre-line;
  color: ${colors.error};
  text-align: center;
`;

const Root = styled(FlexBox)`
  height: 100vh;

  ${() => isMobile && css`
    max-height: -webkit-fill-available;

    ${Section} {
      padding: 24px;
      grid-template-columns: 100%;
      background-color: ${colors.primaryBlue};
    }

    ${Form} {
      padding: 35px 20px;
      width: 100%;
    }
  `}
`;

const BASE_URL = process.env.REACT_APP_BASE_URL;

function Login({
  history,
  formClassName,
}: any) {

  const location = useLocation();
  const searchParams = queryString.parse(location.search);
  const [form, setForm] = useState({
    mobileNumber: '',
    password: '',
  });
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);

  const {
    accessToken,
    isLogging,
    loginStatus,
    loginErrors,
    forgotPasswordSuccess,
  } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  useEffect(() => {
    if (accessToken) {
      history.replace({
        pathname: getNextPathname(searchParams),
        search: location.search,
      });
    }
  }, [accessToken]);

  useEffect(() => {
    if (loginStatus === Status.fulfilled) {
      message.success('Login Successful!');
      history.replace({ pathname: '/queue' });
      setTimeout(() => {
        triggerEvent('login');
        triggerEvent('term_accepted');
        storageService.set('isTermsAccepted', true);
      }, 10);
    }
  }, [loginStatus]);

  useEffect(() => {
    if (forgotPasswordSuccess) {
      message.success(forgotPasswordSuccess);
    }
  }, [forgotPasswordSuccess]);

  useEffect(() => {
    eventService.startActionDuration('login_success');
    storageService.get('isTermsAccepted').then((value: any) => {
      if (value) {
        setIsTermsAccepted(value);
      }
    });
    return () => {
      dispatch(updateAuthState({
        loginStatus: '',
        forgotPasswordSuccess: '',
      }));
    };
  }, []);

  function handleChange({ name, value }: any) {
    if (name === 'mobileNumber') {
      value = value.substring(0, 10);
    }

    setForm({ ...form, [name]: value });
    if (!isEmpty(loginErrors)) {
      dispatch(updateAuthState({ loginErrors: {} }));
    }
  }

  function handleSubmit() {
    if (isLogging) { return; }

    dispatch(login(form));
  }

  function handleResetPassword() {
    switch (true) {
      case !form.mobileNumber:
        message.error('Please enter mobile number');
        break;
      case form.mobileNumber.length !== 10:
        message.error('Please enter a valid mobile number');
        break;
      default:
        dispatch(forgotPassword(form));
        break;
    }
  }

  let loginError = '';
  if (!isEmpty(loginErrors)) {
    loginError = Object.keys(loginErrors).map((key: string) => `${get(loginErrors, `${key}[0]`, '')}`).join('\n');
  }

  function commonInputProps(name: string, label: string) {
    return {
      name,
      label,
      key: name,
      disabled: isLogging,
      value: form[name] || '',
      onChange: handleChange,
    } as any;
  }

  function renderForm() {
    return (
      <Form className={formClassName}>
        <DoconLogo
          width={'120px'}
          mx='auto'
        />

        <DoconInput
          autoFocus
          {...commonInputProps('mobileNumber', 'Mobile Number')}
          type='tel'
          prefix='+91'
        />

        <DoconInput
          {...commonInputProps('password', 'Password')}
          type='password'
          onEnter={handleSubmit}
        />
        <Body2 className={'infoText'} color='currentColor' hidden={isTermsAccepted}>
          <span>By clicking on sign in you agree to our  </span>
          <a href={`${BASE_URL}/terms`}>Terms of Use</a> <span>and our  </span>
          <a href={`${BASE_URL}/privacy`}>Privacy Policy</a>
        </Body2>
        <PrimaryButton
          isBlock
          title='Sign In'
          icon={isLogging ? 'loading' : ''}
          disabled={form.mobileNumber?.length !== 10}
          onClick={handleSubmit}
        />
        <ErrorText title={loginError} />
      </Form>
    );
  }

  function triggerEvent(name) {

    const eventPayload: any = {
      page_name: getPageName(),
      interaction_type: 'load',
      ui_location: 'main_frame',
      event_type: 'page_load',
      source: 'login',
    };

    switch (name) {
      case 'term_accepted':
        eventPayload.terms_accepted = true;
        eventPayload.privacy_accepted = true;
        eventPayload.input_value = 'mobile_number';
        eventPayload.mobile_number = form.mobileNumber;
        break;

      default:
        break;
    }

    cleverTapAnl.event({
      event_name: name,
      event: eventPayload,
    });
  }

  return (
    <Root>
      <Section flex={'0 0 40%'} hidden={isMobileOnly}>
        <Image
          width={'400px'}
          src={'/images/docon-support.png'}
          alt='docon-support'
        />
      </Section>

      <Section flex={1}>
        {renderForm()}
        <LinkButton
          className={'clickable'}
          title='Reset Password'
          onClick={handleResetPassword}
        />
      </Section>
    </Root>
  );
}

export default Login;
